import { useMemo } from "react";

/**
 * Detect an embeddable video URL and return {kind, embedUrl, thumbnail}.
 * Returns null for anything we can't embed inline.
 */
export function detectVideo(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "").replace(/^m\./, "");
    // YouTube — normal watch, shorts, youtu.be
    if (host === "youtube.com" || host === "youtu.be") {
      let id = u.searchParams.get("v");
      if (!id && host === "youtu.be") id = u.pathname.slice(1);
      const shorts = u.pathname.match(/^\/shorts\/([^/?]+)/);
      if (shorts) id = shorts[1];
      if (id) return {
        kind: "youtube", id,
        embedUrl: `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`,
        thumbnail: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
        aspect: shorts ? "9/16" : "16/9",
      };
    }
    // Vimeo
    if (host === "vimeo.com") {
      const id = u.pathname.split("/").filter(Boolean)[0];
      if (id) return {
        kind: "vimeo", id,
        embedUrl: `https://player.vimeo.com/video/${id}`,
        thumbnail: null,
        aspect: "16/9",
      };
    }
    // Facebook / Instagram / TikTok — no reliable public embed, return "link"
    if (host.endsWith("facebook.com") || host.endsWith("fb.watch")) {
      return {
        kind: "facebook", id: null,
        embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`,
        thumbnail: null, aspect: "9/16",
      };
    }
    if (host.endsWith("instagram.com")) {
      return {
        kind: "instagram", id: null,
        embedUrl: `${url.replace(/\/?$/, "/")}embed/`,
        thumbnail: null, aspect: "9/16",
      };
    }
  } catch { /* not a URL */ }
  return null;
}

function VideoTile({ video, caption, credit, testid }) {
  return (
    <figure className="rounded-2xl overflow-hidden border border-black/10 bg-[#241209]/95" data-testid={testid}>
      <div className="relative w-full" style={{ aspectRatio: video.aspect || "16/9" }}>
        <iframe
          src={video.embedUrl}
          title={caption || "Embedded video"}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
      {(caption || credit) && (
        <figcaption className="px-3 py-2 text-[12px] text-[#F5EBDC] bg-[#241209]/95">
          {caption && <div className="line-clamp-2">{caption}</div>}
          {credit && <div className="text-[10px] opacity-70 mt-0.5 font-mono-tech tracking-wider uppercase">{credit}</div>}
        </figcaption>
      )}
    </figure>
  );
}

/**
 * Smart video/photo showcase that reads `videos` (embed-ready) and `bodyLength`.
 *
 * Layouts:
 *  - 0 videos                     → renders nothing
 *  - 1 video AND bodyLength > 800 → floats right of the article body (side-by-side)
 *  - 1 video AND short body       → single full-width tile
 *  - 2 videos                     → 2-across grid on md+
 *  - 3+ videos                    → 2-across grid; last row wraps naturally
 */
export function VideoShowcase({ videos, bodyLength = 0, className = "" }) {
  if (!videos || videos.length === 0) return null;
  const longRead = bodyLength > 800;

  if (videos.length === 1 && longRead) {
    return (
      <div className={`float-none md:float-right md:w-[45%] md:ml-6 md:mb-4 mb-4 ${className}`} data-testid="video-showcase-single-float">
        <VideoTile video={videos[0].video} caption={videos[0].caption} credit={videos[0].credit} testid="video-tile-0" />
      </div>
    );
  }

  if (videos.length === 1) {
    return (
      <div className={`${className} my-6`} data-testid="video-showcase-single">
        <VideoTile video={videos[0].video} caption={videos[0].caption} credit={videos[0].credit} testid="video-tile-0" />
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 my-6 items-start ${className}`} data-testid="video-showcase-grid">
      {videos.map((v, i) => (
        <VideoTile key={i} video={v.video} caption={v.caption} credit={v.credit} testid={`video-tile-${i}`} />
      ))}
    </div>
  );
}

/**
 * Convenience hook to extract playable videos from a story's `media[]`,
 * `sources[]`, `links[]`, and `canonical_url` — de-duplicated.
 */
export function useStoryVideos(story) {
  return useMemo(() => {
    if (!story) return [];
    const seen = new Set();
    const out = [];

    const push = (url, caption, credit) => {
      const v = detectVideo(url);
      if (!v) return;
      const key = v.id || v.embedUrl;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ video: v, caption, credit });
    };

    (story.media || []).forEach((m) => {
      if (m.media_type === "video") push(m.url, m.caption, m.credit);
      // Some links live in media entries with source_url that IS the video
      if (m.source_url) push(m.source_url, m.caption, m.credit);
    });
    (story.sources || []).forEach((s) => {
      if ((s.source_type || "").toLowerCase().includes("video")
          || (s.platform || "").toLowerCase() === "youtube"
          || (s.platform || "").toLowerCase() === "vimeo") {
        push(s.canonical_url || s.url, s.title, s.creator || s.publisher);
      }
    });
    (story.links || []).forEach((L) => push(L.url, null, L.source_domain));
    // canonical_url as a last resort for news_link items pointing at YouTube
    if (story.canonical_url) push(story.canonical_url, null, story.publisher);

    return out;
  }, [story]);
}
